/** 과제/포인트 노트 로컬 선택 기억 — 기기 localStorage만 (DB 없음) */

import {
  loadSelectedClassGroupId,
  saveSelectedClassGroupId,
} from './selectedClassGroupPreference';

export type NoteSelectedGroupScope = 'assignmentPoints' | 'pointItem';

export type NoteMemberLayoutMode = 'number' | 'seat' | 'list';

/** 포인트 코인 아이템: 포인트 / 코인 / 아이템 / 기록 */
export type NoteMemberDisplayMode = 'points' | 'coins' | 'items' | 'notes';

/** 포인트 코인 아이템: 데이터 조회 / 데이터 기록 */
export type NoteMemberInteractionMode = 'view' | 'record';

function layoutStorageKey(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): string {
  return `cp.noteMemberLayout.v1.${scope}.${workspaceId.trim()}.${gradeId.trim()}`;
}

function displayStorageKey(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): string {
  return `cp.noteMemberDisplay.v1.${scope}.${workspaceId.trim()}.${gradeId.trim()}`;
}

function interactionStorageKey(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): string {
  return `cp.noteMemberInteraction.v1.${scope}.${workspaceId.trim()}.${gradeId.trim()}`;
}

/** 전 페이지 공유 학급 선택을 읽는다 (scope는 하위 호환용) */
export function loadNoteSelectedGroupId(
  _scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): string | null {
  return loadSelectedClassGroupId(workspaceId, gradeId);
}

/** 전 페이지 공유 학급 선택으로 저장한다 (scope는 하위 호환용) */
export function saveNoteSelectedGroupId(
  _scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string,
  groupId: string | null
): void {
  saveSelectedClassGroupId(workspaceId, gradeId, groupId);
}

/** 현재 그룹 목록에서 선택값 결정: 우선순위 preferred → 첫 그룹 */
export function resolveNoteSelectedGroupId(
  groupIds: string[],
  preferredIds: Array<string | null | undefined>
): string | null {
  if (groupIds.length === 0) return null;
  const set = new Set(groupIds);
  for (const id of preferredIds) {
    const trimmed = (id ?? '').trim();
    if (trimmed && set.has(trimmed)) return trimmed;
  }
  return groupIds[0] ?? null;
}

export function loadNoteMemberLayoutMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): NoteMemberLayoutMode | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = (window.localStorage.getItem(layoutStorageKey(scope, workspaceId, gradeId)) ?? '')
      .trim();
    if (raw === 'number' || raw === 'seat' || raw === 'list') return raw;
    return null;
  } catch {
    return null;
  }
}

export function saveNoteMemberLayoutMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string,
  mode: NoteMemberLayoutMode
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(layoutStorageKey(scope, workspaceId, gradeId), mode);
  } catch {
    /* ignore quota */
  }
}

export function loadNoteMemberDisplayMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): NoteMemberDisplayMode | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = (window.localStorage.getItem(displayStorageKey(scope, workspaceId, gradeId)) ?? '')
      .trim();
    if (raw === 'points' || raw === 'coins' || raw === 'items' || raw === 'notes') return raw;
    return null;
  } catch {
    return null;
  }
}

export function saveNoteMemberDisplayMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string,
  mode: NoteMemberDisplayMode
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(displayStorageKey(scope, workspaceId, gradeId), mode);
  } catch {
    /* ignore quota */
  }
}

export function loadNoteMemberInteractionMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string
): NoteMemberInteractionMode | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = (
      window.localStorage.getItem(interactionStorageKey(scope, workspaceId, gradeId)) ?? ''
    ).trim();
    if (raw === 'view' || raw === 'record') return raw;
    return null;
  } catch {
    return null;
  }
}

export function saveNoteMemberInteractionMode(
  scope: NoteSelectedGroupScope,
  workspaceId: string,
  gradeId: string,
  mode: NoteMemberInteractionMode
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(interactionStorageKey(scope, workspaceId, gradeId), mode);
  } catch {
    /* ignore quota */
  }
}
